/* eslint-disable */
import React from "react";
import {
  Card,
  Group,
  Title,
  Text,
  Button,
  createStyles,
  SimpleGrid,
} from "@mantine/core";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useGetDepartmentsWithUsers } from "api/department/getEmployeeDepartment";
import { useNavigate } from "react-router-dom";
import { useGetEmployeeCounts } from "api/users/employeestatus";
import { useGetUser } from "api/users/getUser";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const useStyles = createStyles((theme) => ({
  card: {
    borderRadius: 10,
    height: "100%",
    padding: theme.spacing.md,
  },
  totalRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  totalNumber: { fontSize: 13, fontWeight: 800, color: "#0f172a" },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 10,
    display: "inline-block",
    marginRight: 10,
  },
  metricCard: {
    border: "1px solid #ecf0f3",
    padding: 12,
    borderRadius: 8,
    transition: "all 0.3s ease",
    cursor: "pointer",
    "&:hover": {
      transform: "scale(1.05)",
      color: "#fff",
    },
  },
  metricNumber: { fontSize: 32, fontWeight: 800, color: "#0f172a" },
  viewBtn: { width: "100%", marginTop: theme.spacing.md },
}));

interface Segment {
  label: string;
  value: number;
  color: string;
  hoverColor?: string;
}

export default function EmployeeStatusCard() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { data: userData } = useGetUser();

  const { data: departmentsData, isLoading: isDepartmentsLoading } =
    useGetDepartmentsWithUsers();

  const {
    data: employeeCountsResponse,
    isLoading: isEmployeeCountsLoading,
    error: employeeCountsError,
  } = useGetEmployeeCounts(userData?.org?._id || "");

  const apiCounts = employeeCountsResponse?.data ?? null;
  const departments = departmentsData?.data ?? [];

  const isLoading = isEmployeeCountsLoading || isDepartmentsLoading;
  const error = employeeCountsError;

  const totalFromApi = React.useMemo(() => {
    if (apiCounts && typeof apiCounts.totalEmployees === "number") {
      return apiCounts.totalEmployees;
    }
    if (!departments || departments.length === 0) return 0;
    return departments.reduce((acc, d) => {
      const len = Array.isArray(d.members) ? d.members.length : 0;
      return acc + len;
    }, 0);
  }, [apiCounts, departments]);

  const colorPalette = [
    { normal: "#6366f1", hover: "#4f46e5" },
    { normal: "#10b981", hover: "#059669" },
    { normal: "#f59e0b", hover: "#d97706" },
    { normal: "#ef4444", hover: "#dc2626" },
    { normal: "#3b82f6", hover: "#2563eb" },
  ];

  const segments: Segment[] = React.useMemo(() => {
    if (apiCounts?.employmentTypes?.length) {
      return apiCounts.employmentTypes.map((typeObj: any, index: number) => ({
        label: typeObj.employmentType
          ? typeObj.employmentType
              .replace(/-/g, " ")
              .replace(/\b\w/g, (c: any) => c.toUpperCase())
          : "Unknown",
        value: typeObj.count || 0,
        color: colorPalette[index % colorPalette.length].normal,
        hoverColor: colorPalette[index % colorPalette.length].hover,
      }));
    }

    return [{ label: "No Data", value: 0, color: "#d1d5db" }];
  }, [apiCounts]);

  const metrics = React.useMemo(() => {
    const total = segments.reduce((s, x) => s + x.value, 0) || 1;
    return segments.map((s) => {
      const percent = total === 0 ? 0 : Math.round((s.value / total) * 100);
      return {
        label: s.label,
        percent,
        value: s.value,
        color: s.color,
        hoverColor: s.hoverColor,
      };
    });
  }, [segments]);

  const chartData = React.useMemo(() => {
    const datasets = segments.map((s) => ({
      label: s.label,
      data: [s.value],
      backgroundColor: s.color,
      barThickness: 18,
      borderRadius: 8,
      stack: "stack1",
    }));

    return { labels: [""], datasets };
  }, [segments]);

  const chartOptions = React.useMemo(
    () => ({
      indexAxis: "y" as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
      },
      scales: {
        x: { stacked: true, display: false, beginAtZero: true },
        y: { stacked: true, display: false },
      },
      elements: { bar: { borderWidth: 0 } },
    }),
    []
  );

  return (
    <Card className={classes.card} radius="md" shadow="sm">
      <Group position="apart" align="flex-start">
        <Title order={3} style={{ fontSize: "18px" }}>
          Employee Type
        </Title>
      </Group>

      <div style={{ marginTop: 14 }}>
        <div className={classes.totalRow}>
          <Text color="dimmed">Total Employee</Text>
          <div className={classes.totalNumber}>
            {isLoading ? "..." : String(totalFromApi)}
          </div>
        </div>

        <div style={{ marginTop: 12, height: 36 }}>
          {isLoading ? (
            <div style={{ padding: 8 }}>Loading chart...</div>
          ) : error ? (
            <div style={{ padding: 8, color: "red" }}>Failed to load chart</div>
          ) : (
            <Bar data={chartData} options={chartOptions} height={36} />
          )}
        </div>

        <div style={{ marginTop: 14 }}>
          {" "}
          <SimpleGrid cols={2} spacing="sm">
            {" "}
            {metrics.map((m, i) => (
              <div
                key={i}
                style={{
                  border: "1px solid #ecf0f3",
                  padding: 12,
                  borderRadius: 6,
                }}
              >
                {" "}
                <Group position="apart" align="center">
                  {" "}
                  <Group spacing={8}>
                    {" "}
                    <span
                      className={classes.legendDot}
                      style={{ background: m.color }}
                    />{" "}
                    <Text size="sm">{m.label}</Text>{" "}
                    <Text>{String(m.value).padStart(2, "0")}</Text>{" "}
                  </Group>{" "}
                </Group>{" "}
              </div>
            ))}{" "}
          </SimpleGrid>{" "}
        </div>

        <div style={{ marginTop: 20 }}>
          <Button
            variant="light"
            className={classes.viewBtn}
            onClick={() => navigate("/all-employees")}
          >
            View All Employees
          </Button>
        </div>
      </div>
    </Card>
  );
}
